import { createReducer, on } from '@ngrx/store';
import { Parcel } from 'app/core/app.model';
import { fullAddress } from 'app/core/dashboard-page/address.model';
import { initialState, PropertyItem } from 'app/core/properties-table/properties.state';
import { AsyncItem } from 'app/shared/async-item';
import {
    createSummaryReport,
    deleteArrayItem,
    deleteItem,
    deleteSection,
    editableMode,
    editItem,
    editTitle,
    revertChanges,
    summaryTaskFail,
    summaryTaskLoading,
    summaryTaskSuccess,
    updateGoogleStreetViewImage
} from './summary-sheet.actions';
import { EditableBoolean, loadingTask, resolveResult, SummaryData, summaryInitial } from './summary-sheet.model';

type UpdateFn = (ss: SummaryData, parcel: Parcel) => SummaryData;

const isEditable = (v: any): v is (AsyncItem | EditableBoolean) => v instanceof AsyncItem || v instanceof EditableBoolean;

const removeItemOnIndex = <T>(arr: T[], index: number) => [...arr.slice(0, index), ...arr.slice(index + 1)];

const updateSummary = (properties: PropertyItem[], upid: string, updateFn: UpdateFn) =>
    properties.map(prop => {
        if (prop.parcel.upid === upid) {
            return {
                ...prop,
                summary: updateFn(prop.summary || summaryInitial, prop.parcel),
            };
        }
        return prop;
    });


export const summaryReducer = createReducer(
    initialState,
    on(createSummaryReport, (state, { upid, withMlsHistory }) => ({
        ...state,
        properties: updateSummary(state.properties, upid, (s, parcel) => ({
            ...s,
            showMlsHistory: new EditableBoolean(withMlsHistory),
            title: fullAddress(parcel),
            ownerType: new AsyncItem(parcel.ownerType ?? null, 'done', 'text'),
        })),
    })),
    on(summaryTaskLoading, (state, { upid, taskKey }) => ({
        ...state,
        properties: updateSummary(state.properties, upid, s => loadingTask(s, taskKey)),
    })),
    on(summaryTaskSuccess, (state, { upid, taskKey, result }) => ({
        ...state,
        properties: updateSummary(state.properties, upid, s => resolveResult(s, taskKey, { ...result, status: true })),
    })),
    on(summaryTaskFail, (state, { upid, taskKey, reason }) => ({
        ...state,
        properties: updateSummary(state.properties, upid, s => resolveResult(s, taskKey, { reason, status: false })),
    })),
    on(editTitle, (state, { upid, title }) => ({
        ...state,
        properties: updateSummary(state.properties, upid, s => ({
            ...s,
            title,
        })),
    })),
    on(updateGoogleStreetViewImage, (state, { upid, url }) => ({
        ...state,
        properties: updateSummary(state.properties, upid, s => ({
            ...s,
            googleStreetViewImage: url,
        })),
    })),
    on(editableMode, (state, { upid, enabled }) => ({
        ...state,
        properties: updateSummary(state.properties, upid, s => ({
            ...s,
            editing: enabled,
        })),
    })),
    on(revertChanges, (state, { upid }) => ({
        ...state,
        properties: updateSummary(state.properties, upid, (s, parcel) => ({
            ...Object.fromEntries(Object.entries(s).map(([k, v]) => [k, isEditable(v) ? v.restore() : v])) as SummaryData,
            editing: false,
            title: fullAddress(parcel),
        })),
    })),
    on(editItem, (state, { upid, key, newValue }) => ({
        ...state,
        properties: updateSummary(state.properties, upid, s => ({
            ...s,
            [key]: s[key] instanceof AsyncItem ? (s[key] as AsyncItem).edit(newValue) : newValue,
        })),
    })),
    on(deleteItem, (state, { upid, key }) => ({
        ...state,
        properties: updateSummary(state.properties, upid, s => ({
            ...s,
            [key]: s[key] instanceof AsyncItem ? (s[key] as AsyncItem).delete() : s[key],
        })),
    })),
    on(deleteSection, (state, { upid, key }) => ({
        ...state,
        properties: updateSummary(state.properties, upid, s => ({
            ...s,
            [key]: s[key] instanceof EditableBoolean ? (s[key] as EditableBoolean).edit(false) : s[key],
        })),
    })),
    on(deleteArrayItem, (state, { upid, key, index }) => ({
        ...state,
        properties: updateSummary(state.properties, upid, s => ({
            ...s,
            [key]: s[key] instanceof AsyncItem ? (s[key] as AsyncItem).edit(removeItemOnIndex((s[key] as AsyncItem).value, index)) : s[key],
        })),
    }))
);
